<script setup lang="ts">
const props = defineProps<{
    title: string,
    desc: string,
    items: Array<{
        title: string,
        img: string,
        path: string,
        groups: Array<{
            title: string,
            desc: string,
            icon: string,
        }>,
    }>,
}>();
const device = useDevice();
const activeItem = ref(props.items[0]);

const splitedTitle = computed(() => props.title.split("'"));

// Meteors
const meteors = [
    { top: 100, delay: '3s' },
    { top: 200, delay: '5s' },
    { top: 350, delay: '2s' },
    { top: 500, delay: '1s' },
    { top: 650, delay: '4s' },
];
</script>

<template>
    <SectionsBase id="solutions" class="background">
        <div class="absolute w-full h-full overflow-hidden top-0 right-0">
            <IconsLine 
                v-for="meteor in meteors"
                class="meteor" 
                :style="{ top: meteor.top, animationDelay: meteor.delay }"
            />
        </div>
        <div class="container relative z-1 text-white lg:px-20">
            <ChipsDivider>راهکارها</ChipsDivider>
            <h2>
                <template v-if="splitedTitle.length > 1">
                    {{ splitedTitle[0] }}
                    <span class="text-secondary-600">"{{ splitedTitle[1] }}"</span>
                    {{ splitedTitle[2] }}
                </template>
                <template v-else>{{ props.title }}</template>
            </h2>
            <p class="text-body1-semibold text-primary-50 text-center mt-6">{{ props.desc }}</p>
            <ClientOnly>
                <LazySectionsSolutionsCarouselMobile
                    v-if="!device.isDesktop.value"
                    v-model="activeItem"
                    :items="props.items" 
                />
                <LazySectionsSolutionsCarouselDesktop
                    v-else
                    v-model="activeItem"
                    :items="props.items"
                />
                <div class="w-full flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-50 lg:px-10 lg:py-0">
                    <div class="w-full lg:max-w-145 flex flex-col gap-6">
                        <div 
                            v-for="group in activeItem?.groups"
                            :key="group.title"
                            class="w-full"
                        >
                            <div class="flex items-center gap-2.5 mb-2.5">
                                <div class="w-10 h-10 flex-center bg-primary-500/50 rounded-full">
                                    <Icon :name="group.icon" size="24" />
                                </div>
                                <h4 class="text-body1-bold">{{ group.title }}</h4>
                            </div>
                            <p class="text-primary-50 text-body2 pr-12.5" v-html="group.desc" />
                        </div>
                        <LazyButton
                            v-if="activeItem?.path"
                            variant="white"
                            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
                            size="Large"
                            pill
                            link
                            :to="activeItem?.path" 
                            target="_blank" 
                            class="w-fit"
                        >
                            بیشتر بخوانید
                        </LazyButton>
                    </div>
                    <div class="w-full lg:max-w-90 lg:min-w-90 min-h-100 flex-center">
                        <Transition name="fade" mode="out-in">
                            <div class="image-wrapper" :key="activeItem?.img">
                                <img
                                    width="440"
                                    :src="activeItem?.img"
                                    :alt="activeItem?.title"
                                    class="w-full rounded-2xl"
                                />
                            </div>
                        </Transition>
                    </div>
                </div>
            </ClientOnly>
        </div>
    </SectionsBase>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.background {
    position: relative;
    background: linear-gradient(139.35deg, #00204E 8.8%, #003D97 32.61%, #003D97 43.48%, #012C6C 88.8%);
}
.background::before {
    @apply
    content-[''] absolute w-full h-full top-0 left-0 bg-[url('/img/new/backgrounds/mesh-03.webp')] 
    bg-no-repeat bg-center lg:bg-size-[100%_auto]
}

.image-wrapper {
    @apply
    w-full p-2.5 rounded-t-[20px] border border-white border-b-0 shadow-gray-xs relative
}
.image-wrapper::before,
.image-wrapper::after {
    @apply
    content-[''] absolute top-5 w-px h-full bg-linear-to-b from-white to-90% to-[#012C6C] 
    lg:to-[#003D97] z-1
}
.image-wrapper::before {
    @apply
    -right-px
}
.image-wrapper::after {
    @apply
    -left-px
}

.meteor {
    animation: meteor infinite linear;
    animation-duration: 8s;
    @apply
    absolute bg-linear-to-r from-white/30 to-transparent -right-25
}
@keyframes meteor {
    to {
        transform: translateX(-110vw);
    }
}
</style>